import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Card from "../components/ui/Card";

export default function Pricing() {
  return (
    <>
      <Helmet>
        <title>Pricing | UKPropertyChecks</title>
        <meta
          name="description"
          content="UK property checks. Buyer's Report £29. Seller's Pack £79. One-off payment, no subscription."
        />
        <link rel="canonical" href="https://ukpropertychecks.co.uk/pricing" />
        <meta property="og:title" content="Pricing — UK Property Checks" />
        <meta property="og:description" content="UK property checks. Buyer's Report £29. Seller's Pack £79. One-off payment, no subscription." />
        <meta property="og:url" content="https://ukpropertychecks.co.uk/pricing" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: "Seller's document pack",
            description:
              "Solicitor-ready PDF of TA6, TA7 (leasehold only), and LPE1 (leasehold only) forms, generated from a guided disclosure questionnaire and optional document uploads.",
            brand: { "@type": "Brand", name: "UKPropertyChecks" },
            category: "Property conveyancing documents",
            offers: {
              "@type": "Offer",
              url: "https://ukpropertychecks.co.uk/",
              priceCurrency: "GBP",
              price: "79.00",
              priceSpecification: {
                "@type": "PriceSpecification",
                price: "79.00",
                priceCurrency: "GBP",
                valueAddedTaxIncluded: true,
              },
              availability: "https://schema.org/InStock",
              seller: { "@type": "Organization", name: "UKPropertyChecks" },
            },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: "Buyer's Report",
            description:
              "Pre-offer due-diligence report covering Energy Performance Certificate (EPC), flood risk, heritage designations, alteration constraints, planning history, and tax/regulatory rules for a UK property. Sourced from public records.",
            brand: { "@type": "Brand", name: "UKPropertyChecks" },
            category: "Property buyer's report",
            offers: {
              "@type": "Offer",
              url: "https://ukpropertychecks.co.uk/",
              priceCurrency: "GBP",
              price: "29.00",
              priceSpecification: {
                "@type": "PriceSpecification",
                price: "29.00",
                priceCurrency: "GBP",
                valueAddedTaxIncluded: true,
              },
              availability: "https://schema.org/InStock",
              seller: { "@type": "Organization", name: "UKPropertyChecks" },
            },
          })}
        </script>
      </Helmet>

      <main className="bg-white">
        <div className="container-main section-spacing">
          <h1>Pricing</h1>
          <p className="mt-3 text-slate-800">
            One-off payments. No subscriptions.
          </p>

          <div className="mt-8 space-y-6">
            {/* Selling documents first (primary) */}
            <div className="p-6 bg-emerald-50 border border-emerald-200 rounded-xl">
              <div className="flex items-start justify-between">
                <div>
                  <h2>Seller's Pack</h2>
                  <p className="mt-1 text-sm text-slate-600">TA6, TA7, and LPE1 forms prepared from a guided questionnaire</p>
                </div>
                <div className="text-right shrink-0 ml-4">
                  <p className="text-3xl font-semibold text-slate-950">£79</p>
                  <p className="text-xs text-slate-600">one-off payment</p>
                </div>
              </div>

              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <div>
                  <h3 className="text-sm font-semibold text-slate-950 mb-2">Forms included</h3>
                  <ul className="text-sm text-slate-700 space-y-1.5">
                    <li className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      TA6 Property Information Form (59 questions)
                    </li>
                    <li className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      TA7 Leasehold Information Form (leasehold only)
                    </li>
                    <li className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      LPE1 Leasehold Property Enquiries (leasehold only)
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-950 mb-2">Features</h3>
                  <ul className="text-sm text-slate-700 space-y-1.5">
                    <li className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      Guided disclosure questionnaire
                    </li>
                    <li className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      Official-format PDF with section numbering
                    </li>
                    <li className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      Source trail showing where each answer came from
                    </li>
                    <li className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      Generated PDF available in your account for 12 months
                    </li>
                  </ul>
                </div>
              </div>

              {/* Solicitor comparison */}
              <Card variant="subtle" padding="sm" className="mt-6">
                <p className="text-sm text-slate-700">
                  Solicitors typically charge £300–£500 to prepare these forms. We prepare the answers in advance from your questionnaire and uploaded documents. Your solicitor reviews and submits the final version.
                </p>
              </Card>

              <div className="mt-6">
                <Link to="/" className="btn-primary">Start your Seller's Pack</Link>
              </div>
            </div>

            {/* Buyer's Report second (secondary) */}
            <Card variant="subtle">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-slate-700">Buyer's Report</h2>
                  <p className="mt-1 text-sm text-slate-600">Pre-offer due-diligence covering EPC, flood risk, heritage, alterations, planning history, and tax rules</p>
                </div>
                <div className="text-right shrink-0 ml-4">
                  <p className="text-2xl font-semibold text-slate-700">£29</p>
                  <p className="text-xs text-slate-600">one-off</p>
                </div>
              </div>
              <ul className="mt-4 text-sm text-slate-600 space-y-1.5">
                <li className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  Energy Performance Certificate (EPC) — current and potential rating
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  Flood risk — rivers and sea zone, plus surface water
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  Heritage designations — listed buildings, conservation areas
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  Alteration and extension constraints — Green Belt, Area of Outstanding Natural Beauty (AONB), Tree Preservation Orders (TPOs), Article 4 directions
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  Planning application history — last 10 years
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  Tax and regulatory rules — Stamp Duty Land Tax (SDLT) bands, council tax band, leasehold reform, Section 24
                </li>
              </ul>
              <p className="mt-3 text-xs text-slate-600">
                Title summary is not yet included. We'll add it once HM Land Registry integration is live.
              </p>
              <div className="mt-4">
                <Link to="/" className="text-sm text-blue-700 hover:text-blue-800 hover:underline">Start a Buyer's Report →</Link>
              </div>
            </Card>

            <div className="text-center pt-2">
              <Link to="/samples" className="text-sm text-blue-700 hover:text-blue-900 underline">
                View sample reports before you buy
              </Link>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
